import { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from '@tanstack/react-router';
import {
  Receipt,
  Plus,
  Trash2,
  CheckCircle2,
  Ban,
  Wallet,
  AlertTriangle,
  Download,
  TimerReset,
  Settings2,
  PlayCircle,
  ListTree,
  FileText,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, api, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatDateTime } from '@/lib/utils';

type Status = 'unpaid' | 'paid' | 'overdue' | 'waived';

interface BillLineItem {
  id: string;
  charge_type_id: string;
  label: string;
  amount_inr: number;
  // Phase 3 — per-line breakdown so receipts can render base + GST cleanly.
  // Older rows may have these null; UI must fallback to `amount_inr` when so.
  base_amount_inr?: number | null;
  gst_pct?: number | null;
  gst_amount_inr?: number | null;
  created_at: string;
  charge_type?: { id: string; code: string; label: string };
}

interface Bill {
  id: string;
  society_id: string;
  unit_id: string;
  period_label: string;
  amount_inr: number;
  due_date: string;
  status: Status;
  paid_at: string | null;
  notes: string | null;
  late_fee_inr: number;
  late_fee_applied_at: string | null;
  late_fee_inr_computed: number;
  total_due_inr: number;
  receipt_number: string | null;
  // Phase 3 — denormalised totals.
  subtotal_inr?: number | null;
  gst_total_inr?: number | null;
  rebate_total_inr?: number | null;
  created_at: string;
  updated_at: string;
  unit?: {
    id: string;
    number: string;
    block: { id: string; name: string } | null;
  };
}

type PaymentMethod = 'razorpay' | 'upi' | 'cash' | 'cheque' | 'neft' | 'manual' | 'card';

interface PaymentReceiptRow {
  id: string;
  bill_id: string;
  unit_id: string;
  receipt_number: string;
  receipt_date: string;
  amount_inr: number;
  payment_method: PaymentMethod;
  payment_ref: string | null;
  notes: string | null;
}

interface PaymentReceiptListResponse {
  items: PaymentReceiptRow[];
  next_cursor?: string | null;
}

const PAYMENT_METHOD_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'neft', label: 'NEFT' },
  { value: 'manual', label: 'Manual / Other' }
];

interface BillListResponse {
  items: Bill[];
  next_cursor?: string | null;
}

interface BillingPolicy {
  society_id: string;
  cycle_frequency: 'monthly' | 'quarterly' | 'annual';
  default_amount_inr: number;
  per_unit_overrides: Record<string, number>;
  generation_day_of_month: number;
  due_day_of_month: number;
  late_fee_grace_days: number;
  late_fee_inr_flat: number;
  late_fee_inr_percent: number;
  late_fee_cap_percent: number;
  auto_generate_enabled: boolean;
  auto_late_fee_enabled: boolean;
  last_cycle_run_at: string | null;
  last_cycle_period_label: string | null;
  last_late_fee_sweep_at: string | null;
}

interface UnitOption {
  id: string;
  block_id: string | null;
  number: string;
}

interface BlockOption {
  id: string;
  name: string;
}

const STATUS_LABEL: Record<Status, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
  overdue: 'Overdue',
  waived: 'Waived'
};

const STATUS_VARIANT: Record<Status, 'info' | 'ok' | 'alert' | 'muted'> = {
  unpaid: 'info',
  paid: 'ok',
  overdue: 'alert',
  waived: 'muted'
};

// Mirrors apps/api/src/modules/bills/routes.ts CreateSchema. Keep in sync.
const AMOUNT_MIN_INR = 1;
const AMOUNT_MAX_INR = 1_000_000;
const NOTES_MAX = 500;

// Same regex as the server. `YYYY-MM` (2000-2099) OR "<Month> <YYYY>", with
// optional trailing free-text suffix (≤20 chars, alnum/space/underscore/dash).
const PERIOD_LABEL_RE =
  /^(?:(?:20\d{2}-(?:0[1-9]|1[0-2]))|(?:(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+20\d{2}))(?:\s+[A-Za-z0-9 _-]{1,20})?$/;

const CreateBillSchema = z.object({
  unit_id: z.string().uuid('Select a flat'),
  period_label: z
    .string()
    .trim()
    .min(2, 'Period required')
    .max(40)
    .regex(PERIOD_LABEL_RE, 'Use "YYYY-MM" (e.g. 2026-06) or "May 2026"'),
  amount_inr: z.coerce
    .number({ invalid_type_error: 'Amount required' })
    .int('Whole rupees only')
    .min(AMOUNT_MIN_INR, `Must be at least ₹${AMOUNT_MIN_INR}`)
    .max(AMOUNT_MAX_INR, `Must not exceed ₹${AMOUNT_MAX_INR.toLocaleString('en-IN')}`),
  due_date: z.string().min(1, 'Due date required'),
  notes: z
    .string()
    .trim()
    .max(NOTES_MAX, `Max ${NOTES_MAX} characters`)
    .optional()
});
type CreateBillForm = z.infer<typeof CreateBillSchema>;

function formatInr(rupees: number): string {
  return `₹${rupees.toLocaleString('en-IN')}`;
}

/**
 * Build a list of YYYY-MM period labels: 6 months back through 6 ahead,
 * newest first. Cheap (12 entries) so we rebuild on render rather than memo.
 */
function buildRecentPeriodOptions(span: number): Array<{ value: string; label: string }> {
  const now = new Date();
  const out: Array<{ value: string; label: string }> = [];
  const half = Math.floor(span / 2);
  for (let i = -half; i <= span - half; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const label = d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    out.push({ value: `${yyyy}-${mm}`, label: `${yyyy}-${mm} · ${label}` });
  }
  return out.reverse();
}

function unitLabel(b: Bill): string {
  if (!b.unit) return '—';
  const block = b.unit.block?.name ?? '—';
  return `${block} / ${b.unit.number}`;
}

/**
 * Billing config card — sits above the bills table. Admin/treasurer can:
 *   - Toggle auto-generate + auto-late-fee
 *   - Tune cycle days / late-fee math
 *   - Fire "Run cycle now" + "Run late-fee sweep now"
 *
 * Accountant gets a read-only view (no editing, no manual triggers).
 */
function BillingConfigCard(): JSX.Element {
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canWrite =
    role === 'admin' || role === 'treasurer' || role === 'super_admin';

  const policyQuery = useQuery({
    queryKey: ['billing-policy'],
    queryFn: () => get<BillingPolicy>('/billing-policy')
  });
  const policy = policyQuery.data;

  // Local form state mirrors the server policy and is rebuilt on every
  // successful refetch via `useMemo` — no useEffect required.
  const [draft, setDraft] = useState<Partial<BillingPolicy>>({});
  const merged = useMemo<BillingPolicy | null>(
    () => (policy ? ({ ...policy, ...draft } as BillingPolicy) : null),
    [policy, draft]
  );

  const updateMut = useMutation({
    mutationFn: (body: Partial<BillingPolicy>) => patch<BillingPolicy>('/billing-policy', body),
    onSuccess: () => {
      toast.success('Billing policy saved');
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['billing-policy'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const runCycleMut = useMutation({
    mutationFn: () =>
      post<{ bills_generated: number; skipped: number; period_label: string }>(
        '/billing-policy/run-cycle-now',
        {}
      ),
    onSuccess: (res) => {
      toast.success(
        `Cycle ${res.period_label}: ${res.bills_generated} generated, ${res.skipped} skipped`
      );
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
      void queryClient.invalidateQueries({ queryKey: ['billing-policy'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const runSweepMut = useMutation({
    mutationFn: () =>
      post<{ bills_marked_overdue: number }>('/billing-policy/run-late-fee-sweep-now', {}),
    onSuccess: (res) => {
      toast.success(`Late-fee sweep: ${res.bills_marked_overdue} bills marked overdue`);
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
      void queryClient.invalidateQueries({ queryKey: ['billing-policy'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  if (policyQuery.isLoading || !merged) {
    return (
      <Card>
        <CardContent className="p-vt-4">
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const setField = <K extends keyof BillingPolicy>(key: K, value: BillingPolicy[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const toggleField = (key: 'auto_generate_enabled' | 'auto_late_fee_enabled', value: boolean) => {
    if (!canWrite) return;
    // Toggles save immediately — no need to wait for "Save" click.
    setField(key, value);
    updateMut.mutate({ [key]: value });
  };

  const onSave = () => {
    if (!canWrite || Object.keys(draft).length === 0) return;
    updateMut.mutate(draft);
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-vt-4 p-vt-4">
        <div className="flex items-center gap-vt-2">
          <Settings2 className="h-5 w-5 text-vt-text-muted" strokeWidth={1.75} />
          <div className="flex-1">
            <div className="text-vt-16 font-semibold text-vt-text">Billing config</div>
            <div className="text-vt-12 text-vt-text-muted">
              Auto-generate monthly bills + apply late fees. Daily worker runs 02:00 + 03:00 IST.
            </div>
          </div>
        </div>

        {/* Toggles */}
        <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
          <label className="flex items-center justify-between gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <span className="flex flex-col gap-0.5">
              <span className="text-vt-13 font-medium text-vt-text">Auto-generate monthly bills</span>
              <span className="text-vt-12 text-vt-text-muted">
                Worker runs on day {merged.generation_day_of_month} of each month
              </span>
            </span>
            <input
              type="checkbox"
              className="h-5 w-5 cursor-pointer accent-vt-primary"
              disabled={!canWrite || updateMut.isPending}
              checked={merged.auto_generate_enabled}
              onChange={(e) => toggleField('auto_generate_enabled', e.target.checked)}
            />
          </label>

          <label className="flex items-center justify-between gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <span className="flex flex-col gap-0.5">
              <span className="text-vt-13 font-medium text-vt-text">Auto-apply late fees</span>
              <span className="text-vt-12 text-vt-text-muted">
                Sweeps unpaid bills past grace ({merged.late_fee_grace_days}d)
              </span>
            </span>
            <input
              type="checkbox"
              className="h-5 w-5 cursor-pointer accent-vt-primary"
              disabled={!canWrite || updateMut.isPending}
              checked={merged.auto_late_fee_enabled}
              onChange={(e) => toggleField('auto_late_fee_enabled', e.target.checked)}
            />
          </label>
        </div>

        {/* Number inputs */}
        <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_default_amount">Default amount (₹/month)</Label>
            <Input
              id="bp_default_amount"
              type="number"
              min={1}
              max={1_000_000}
              step={1}
              disabled={!canWrite}
              value={merged.default_amount_inr}
              onChange={(e) => setField('default_amount_inr', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_gen_day">Generation day</Label>
            <Input
              id="bp_gen_day"
              type="number"
              min={1}
              max={28}
              step={1}
              disabled={!canWrite}
              value={merged.generation_day_of_month}
              onChange={(e) => setField('generation_day_of_month', parseInt(e.target.value, 10) || 1)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_due_day">Due day</Label>
            <Input
              id="bp_due_day"
              type="number"
              min={1}
              max={28}
              step={1}
              disabled={!canWrite}
              value={merged.due_day_of_month}
              onChange={(e) => setField('due_day_of_month', parseInt(e.target.value, 10) || 1)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_grace">Late-fee grace (days)</Label>
            <Input
              id="bp_grace"
              type="number"
              min={0}
              max={90}
              step={1}
              disabled={!canWrite}
              value={merged.late_fee_grace_days}
              onChange={(e) => setField('late_fee_grace_days', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_fee_flat">Late fee — flat (₹)</Label>
            <Input
              id="bp_fee_flat"
              type="number"
              min={0}
              max={1_000_000}
              step={1}
              disabled={!canWrite}
              value={merged.late_fee_inr_flat}
              onChange={(e) => setField('late_fee_inr_flat', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_fee_pct">Late fee — percent (%)</Label>
            <Input
              id="bp_fee_pct"
              type="number"
              min={0}
              max={100}
              step={1}
              disabled={!canWrite}
              value={merged.late_fee_inr_percent}
              onChange={(e) => setField('late_fee_inr_percent', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_fee_cap">Late fee — cap (%)</Label>
            <Input
              id="bp_fee_cap"
              type="number"
              min={0}
              max={100}
              step={1}
              disabled={!canWrite}
              value={merged.late_fee_cap_percent}
              onChange={(e) => setField('late_fee_cap_percent', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="bp_cycle_freq">Cycle frequency</Label>
            <Select
              id="bp_cycle_freq"
              disabled={!canWrite}
              value={merged.cycle_frequency}
              onChange={(e) =>
                setField('cycle_frequency', e.target.value as BillingPolicy['cycle_frequency'])
              }
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </Select>
          </div>
        </div>

        {/* Last-run summary */}
        <div className="grid grid-cols-1 gap-vt-2 rounded-vt-md bg-vt-surface-2 p-vt-3 text-vt-12 md:grid-cols-3">
          <div>
            <div className="text-vt-text-muted">Last cycle</div>
            <div className="font-medium text-vt-text">
              {merged.last_cycle_run_at ? formatDateTime(merged.last_cycle_run_at) : 'Never'}
              {merged.last_cycle_period_label ? ` · ${merged.last_cycle_period_label}` : ''}
            </div>
          </div>
          <div>
            <div className="text-vt-text-muted">Last late-fee sweep</div>
            <div className="font-medium text-vt-text">
              {merged.last_late_fee_sweep_at ? formatDateTime(merged.last_late_fee_sweep_at) : 'Never'}
            </div>
          </div>
          <div>
            <div className="text-vt-text-muted">Your role</div>
            <div className="font-medium text-vt-text">
              {role ?? '—'} {canWrite ? '' : '(read-only)'}
            </div>
          </div>
        </div>

        {canWrite ? (
          <div className="flex flex-wrap items-center justify-end gap-vt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={runCycleMut.isPending}
              onClick={() => runCycleMut.mutate()}
            >
              <PlayCircle className="h-4 w-4" /> Run cycle now
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={runSweepMut.isPending}
              onClick={() => runSweepMut.mutate()}
            >
              <TimerReset className="h-4 w-4" /> Run late-fee sweep now
            </Button>
            <Button
              size="sm"
              disabled={updateMut.isPending || Object.keys(draft).length === 0}
              onClick={onSave}
            >
              Save changes
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// URL-synced filter+sort+page state for bills.
const BILL_FILTER_DEFAULTS = {
  search: '',
  status: '',
  period_label: '',
  unit_id: '',
  amount_min: '',
  amount_max: '',
  sort: 'due_date_desc',
  page_size: 50
};

export function BillsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  // Bill currently shown in the breakdown dialog (null = closed).
  const [breakdownBill, setBreakdownBill] = useState<Bill | null>(null);
  // Confirm dialogs for destructive ops (replaces native window.confirm/prompt).
  const [waiveTarget, setWaiveTarget] = useState<Bill | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Bill | null>(null);
  // Phase 3 — bill we're issuing a receipt against (null = closed).
  const [receiptTarget, setReceiptTarget] = useState<Bill | null>(null);
  const [receiptForm, setReceiptForm] = useState<{
    payment_method: PaymentMethod;
    payment_ref: string;
    notes: string;
  }>({ payment_method: 'cash', payment_ref: '', notes: '' });
  const [filterState, setFilterState] = useUrlSearchState(BILL_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Detail query: only runs when the breakdown dialog is open. Fetches the
  // full bill incl. line_items array (the list endpoint doesn't include them).
  const breakdownQuery = useQuery({
    queryKey: ['bill-detail', breakdownBill?.id],
    queryFn: () =>
      get<Bill & { line_items: BillLineItem[] }>(`/bills/${breakdownBill!.id}`),
    enabled: Boolean(breakdownBill?.id)
  });

  // Receipt existence check — fires when the breakdown dialog opens so the
  // "Generate / View receipt" button knows which mode to render. Cheap call
  // (max 1 row returned). Cached per-bill-id so re-opening is instant.
  const receiptForBillQuery = useQuery({
    queryKey: ['receipts', 'by-bill', breakdownBill?.id],
    queryFn: () =>
      get<PaymentReceiptListResponse>('/receipts', {
        params: { bill_id: breakdownBill!.id, limit: 1 }
      }),
    enabled: Boolean(breakdownBill?.id)
  });
  const existingReceipt = receiptForBillQuery.data?.items?.[0] ?? null;

  // Debounce free-text search → URL state.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const billQueryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      limit: filterState.page_size,
      sort: filterState.sort
    };
    if (filterState.status) params.status = filterState.status;
    if (filterState.period_label) params.period_label = filterState.period_label;
    if (filterState.unit_id) params.unit_id = filterState.unit_id;
    if (filterState.amount_min) {
      const n = parseInt(filterState.amount_min, 10);
      if (!Number.isNaN(n)) params.amount_min = n;
    }
    if (filterState.amount_max) {
      const n = parseInt(filterState.amount_max, 10);
      if (!Number.isNaN(n)) params.amount_max = n;
    }
    if (filterState.search) params.q = filterState.search;
    return params;
  }, [filterState]);

  // Stable hash of every filter that should reset pagination when it changes.
  const filtersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.status,
        filterState.period_label,
        filterState.unit_id,
        filterState.amount_min,
        filterState.amount_max,
        filterState.sort,
        filterState.page_size
      ]),
    [filterState]
  );

  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const list = useQuery({
    queryKey: ['bills', billQueryParams, cursor],
    queryFn: () =>
      get<BillListResponse>('/bills', {
        params: { ...billQueryParams, ...(cursor ? { cursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<Bill>(list.data, cursor, filtersHash);

  const bills = paginator.items;

  // Units + blocks for the Add modal AND for the unit_id filter chip — fetch
  // eagerly so the filter dropdown populates immediately.
  const blocksQuery = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<{ items: BlockOption[] }>('/society/blocks')
  });
  const unitsQuery = useQuery({
    queryKey: ['society', 'units', 'all-bills'],
    queryFn: () => get<{ items: UnitOption[] }>('/society/units', { params: { limit: 200 } })
  });

  const blockNameById = useMemo(() => {
    const map = new Map<string, string>();
    (blocksQuery.data?.items ?? []).forEach((b) => map.set(b.id, b.name));
    return map;
  }, [blocksQuery.data]);

  const unitOptions = useMemo(() => {
    const units = unitsQuery.data?.items ?? [];
    return [...units]
      .map((u) => ({
        id: u.id,
        label: `${u.block_id ? blockNameById.get(u.block_id) ?? '?' : '—'} / ${u.number}`,
        sortKey: `${u.block_id ? blockNameById.get(u.block_id) ?? 'zzz' : 'zzz'}-${u.number}`
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }));
  }, [unitsQuery.data, blockNameById]);

  const form = useForm<CreateBillForm>({
    resolver: zodResolver(CreateBillSchema),
    mode: 'onChange',
    defaultValues: { unit_id: '', period_label: '', amount_inr: 0, due_date: '', notes: '' }
  });

  const create = useMutation({
    mutationFn: (values: CreateBillForm) =>
      post<Bill>('/bills', {
        unit_id: values.unit_id,
        period_label: values.period_label.trim(),
        amount_inr: values.amount_inr,
        // Backend wants ISO 8601 datetime — convert YYYY-MM-DD to midnight UTC.
        due_date: new Date(`${values.due_date}T00:00:00.000Z`).toISOString(),
        ...(values.notes?.trim() ? { notes: values.notes.trim() } : {})
      }),
    onSuccess: (b) => {
      toast.success(`Bill generated for ${b.period_label}`);
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
      setOpen(false);
      form.reset({ unit_id: '', period_label: '', amount_inr: 0, due_date: '', notes: '' });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: Status; notes?: string }) =>
      patch<Bill>(`/bills/${id}`, { status, ...(notes ? { notes } : {}) }),
    onSuccess: (b) => {
      toast.success(`Bill marked ${STATUS_LABEL[b.status].toLowerCase()}`);
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/bills/${id}`),
    onSuccess: () => {
      toast.success('Bill deleted');
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const applyLateFee = useMutation({
    mutationFn: (id: string) => post<Bill>(`/bills/${id}/apply-late-fee`, {}),
    onSuccess: (b) => {
      toast.success(`Late fee ₹${b.late_fee_inr} applied`);
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const openReceipt = (bill: Bill) => {
    // Receipt is by payment_id, so we look up the captured payment for this bill.
    // For simplicity in V0.2 we open a window keyed on receipt_number; admin can also
    // grab the URL from the payments list. The /payments?bill_id=... API gives us
    // the captured payment id.
    void (async () => {
      try {
        const res = await get<{ items: Array<{ id: string; status: string }> }>(`/payments`, {
          params: { bill_id: bill.id, limit: 50 }
        });
        const captured = res.items.find((p) => p.status === 'captured');
        if (!captured) {
          toast.error('No captured payment found for this bill');
          return;
        }
        const base = (import.meta.env.VITE_API_URL ?? 'http://localhost:4100') + '/v1';
        window.open(`${base}/payments/${captured.id}/receipt.html`, '_blank', 'noopener');
      } catch (e) {
        toast.error(getErrorMessage(e));
      }
    })();
  };

  /**
   * Open the Phase 3 PaymentReceipt PDF in a new tab. The endpoint requires
   * a bearer token, so we fetch as blob and present via an object URL.
   */
  const openReceiptPdf = (receiptId: string) => {
    void (async () => {
      try {
        const res = await api.get<Blob>(`/receipts/${receiptId}/pdf`, {
          responseType: 'blob'
        });
        const url = URL.createObjectURL(res.data);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        toast.error(getErrorMessage(e));
      }
    })();
  };

  // Issue a receipt against the currently-selected bill. On success we open
  // the PDF + refresh both the bill list (status may flip to paid) and the
  // receipt-existence cache.
  const issueReceipt = useMutation({
    mutationFn: (vars: {
      bill_id: string;
      amount_inr: number;
      payment_method: PaymentMethod;
      payment_ref?: string;
      notes?: string;
    }) =>
      post<PaymentReceiptRow>('/receipts', {
        bill_id: vars.bill_id,
        amount_inr: vars.amount_inr,
        payment_method: vars.payment_method,
        ...(vars.payment_ref ? { payment_ref: vars.payment_ref } : {}),
        ...(vars.notes ? { notes: vars.notes } : {})
      }),
    onSuccess: (r) => {
      toast.success(`Receipt ${r.receipt_number} issued`);
      void queryClient.invalidateQueries({ queryKey: ['bills'] });
      void queryClient.invalidateQueries({ queryKey: ['bill-detail'] });
      void queryClient.invalidateQueries({ queryKey: ['receipts'] });
      void queryClient.invalidateQueries({ queryKey: ['receipts', 'by-bill'] });
      setReceiptTarget(null);
      setReceiptForm({ payment_method: 'cash', payment_ref: '', notes: '' });
      openReceiptPdf(r.id);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  /**
   * Quick map for fetching a receipt on a list row (badge → PDF). We can't
   * query per-row eagerly without N+1, so the row only knows about the
   * Bill-side `receipt_number` flag; on click we resolve to receipt id.
   */
  const openReceiptByBill = (bill: Bill) => {
    void (async () => {
      try {
        const res = await get<PaymentReceiptListResponse>('/receipts', {
          params: { bill_id: bill.id, limit: 1 }
        });
        const r = res.items?.[0];
        if (!r) {
          toast.error('Receipt record not found for this bill');
          return;
        }
        openReceiptPdf(r.id);
      } catch (e) {
        toast.error(getErrorMessage(e));
      }
    })();
  };

  const onSubmit = (v: CreateBillForm) => {
    create.mutate(v);
  };

  const closeDialog = () => {
    setOpen(false);
    form.reset({ unit_id: '', period_label: '', amount_inr: 0, due_date: '', notes: '' });
  };

  // KPI maths run on the currently-fetched list. With status filter "all"
  // this is the full picture; with a filter applied the cards reflect filtered scope.
  const totalCount = bills.length;
  const outstanding = bills
    .filter((b) => b.status === 'unpaid' || b.status === 'overdue')
    .reduce((s, b) => s + b.amount_inr, 0);
  const paidTotal = bills.filter((b) => b.status === 'paid').reduce((s, b) => s + b.amount_inr, 0);

  const formDialog = (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4" /> Generate bill
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate maintenance bill</DialogTitle>
          <DialogDescription>
            Single-bill entry. Bulk generation will arrive in a later release.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="unit_id">Flat</Label>
            <Select
              id="unit_id"
              disabled={unitsQuery.isLoading}
              aria-invalid={Boolean(form.formState.errors.unit_id) || undefined}
              {...form.register('unit_id')}
            >
              <option value="">{unitsQuery.isLoading ? 'Loading flats…' : 'Select flat'}</option>
              {unitOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.label}</option>
              ))}
            </Select>
            {form.formState.errors.unit_id ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.unit_id.message}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="period_label">Period</Label>
              <Input
                id="period_label"
                placeholder="2026-06"
                aria-invalid={Boolean(form.formState.errors.period_label) || undefined}
                {...form.register('period_label')}
              />
              {form.formState.errors.period_label ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.period_label.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amount_inr">Amount (₹)</Label>
              <Input
                id="amount_inr"
                type="number"
                min={AMOUNT_MIN_INR}
                max={AMOUNT_MAX_INR}
                step={1}
                placeholder="3500"
                aria-invalid={Boolean(form.formState.errors.amount_inr) || undefined}
                {...form.register('amount_inr')}
              />
              {form.formState.errors.amount_inr ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.amount_inr.message}</p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="due_date">Due date</Label>
            <Input
              id="due_date"
              type="date"
              aria-invalid={Boolean(form.formState.errors.due_date) || undefined}
              {...form.register('due_date')}
            />
            {form.formState.errors.due_date ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.due_date.message}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              rows={3}
              placeholder="Includes parking + clubhouse upkeep"
              {...form.register('notes')}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={create.isPending}
              loadingText="Generating…"
              disabled={create.isPending || !form.formState.isValid}
            >
              Generate bill
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const billSortOptions = [
    { value: 'due_date_desc', label: 'Due date (latest)' },
    { value: 'due_date_asc', label: 'Due date (earliest)' },
    { value: 'amount_desc', label: 'Amount (high → low)' },
    { value: 'amount_asc', label: 'Amount (low → high)' },
    { value: 'created_desc', label: 'Created (newest)' },
    { value: 'period_asc', label: 'Period (oldest)' },
    { value: 'period_desc', label: 'Period (newest)' }
  ];

  const billFilterChips: FilterChip[] = [
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      value: filterState.status,
      options: [
        { value: '', label: 'All' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'waived', label: 'Waived' }
      ],
      onChange: (v) => setFilterState({ status: v })
    },
    {
      id: 'period_label',
      label: 'Period (YYYY-MM)',
      type: 'select',
      value: filterState.period_label,
      // Build a 12-month sliding window of recent + upcoming periods so admin
      // can hop months without typing. Exact match on period_label string.
      options: [
        { value: '', label: 'All periods' },
        ...buildRecentPeriodOptions(12)
      ],
      onChange: (v) => setFilterState({ period_label: v })
    },
    {
      id: 'unit_id',
      label: 'Unit',
      type: 'select',
      value: filterState.unit_id,
      options: [
        { value: '', label: 'All units' },
        ...unitOptions.map((u) => ({ value: u.id, label: u.label }))
      ],
      onChange: (v) => setFilterState({ unit_id: v })
    },
    {
      id: 'amount_range',
      label: 'Amount (₹)',
      type: 'number_range',
      value: { min: filterState.amount_min, max: filterState.amount_max },
      placeholderMin: 'Min',
      placeholderMax: 'Max',
      onChange: (v) => setFilterState({ amount_min: v.min, amount_max: v.max })
    }
  ];

  const billHasActiveFilters =
    filterState.search !== BILL_FILTER_DEFAULTS.search ||
    filterState.status !== BILL_FILTER_DEFAULTS.status ||
    filterState.period_label !== BILL_FILTER_DEFAULTS.period_label ||
    filterState.unit_id !== BILL_FILTER_DEFAULTS.unit_id ||
    filterState.amount_min !== BILL_FILTER_DEFAULTS.amount_min ||
    filterState.amount_max !== BILL_FILTER_DEFAULTS.amount_max ||
    filterState.sort !== BILL_FILTER_DEFAULTS.sort ||
    filterState.page_size !== BILL_FILTER_DEFAULTS.page_size;

  const resetBillFilters = () => {
    setFilterState(BILL_FILTER_DEFAULTS);
    setSearchInput('');
  };

  const headerActions = (
    <div className="flex flex-wrap items-center gap-vt-2">
      <Button
        size="sm"
        variant="secondary"
        title="Open the one-time charges page, scoped to the currently-filtered unit if any"
        onClick={() => {
          // Forward the active unit filter as a search param so the
          // sibling page can pre-filter. Cast: TanStack router types are
          // very tight here, and an extra search param is harmless.
          if (filterState.unit_id) {
            void navigate({
              to: '/one-time-charges',
              search: { unit_id: filterState.unit_id }
            } as never);
          } else {
            void navigate({ to: '/one-time-charges' });
          }
        }}
      >
        <Sparkles className="h-4 w-4" /> Issue one-time charge
      </Button>
      {formDialog}
    </div>
  );

  return (
    <>
      <PageHeader
        title="Bills"
        description="Maintenance bills — generate, track, and mark paid."
        actions={headerActions}
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* Billing config (auto-cycle + late-fee sweep) */}
        <BillingConfigCard />

        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Receipt className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Total bills</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : totalCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-alert-100)] text-vt-alert">
                <AlertTriangle className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Outstanding</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-20" /> : formatInr(outstanding)}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <Wallet className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Paid</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-20" /> : formatInr(paidTotal)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Advanced filter toolbar — search + filters + sort + page size */}
        <AdvancedListToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search bill number or unit…"
          filters={billFilterChips}
          sort={{
            value: filterState.sort,
            onChange: (v) => setFilterState({ sort: v }),
            options: billSortOptions
          }}
          pageSize={filterState.page_size}
          onPageSizeChange={(n) => setFilterState({ page_size: n })}
          onReset={resetBillFilters}
          hasActiveFilters={billHasActiveFilters}
          resultLabel={`${bills.length} bill${bills.length === 1 ? '' : 's'}`}
        />

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {list.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <EmptyState
                icon={<Receipt className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load bills"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : bills.length === 0 ? (
              billHasActiveFilters ? (
                <EmptyState
                  icon={<Receipt className="h-6 w-6" strokeWidth={1.75} />}
                  title="No bills match your filters"
                  description="Try clearing filters, or generate a new bill above."
                  action={
                    <Button variant="secondary" onClick={resetBillFilters}>
                      Reset filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Receipt className="h-6 w-6" strokeWidth={1.75} />}
                  title="No bills"
                  description="No maintenance bills have been generated yet."
                  action={
                    <Button onClick={() => setOpen(true)}>
                      <Plus className="h-4 w-4" /> Generate first bill
                    </Button>
                  }
                />
              )
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Receipt</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell>
                        <div className="flex items-center gap-vt-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
                            <Receipt className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-vt-text">{b.period_label}</div>
                            {b.notes ? (
                              <div className="line-clamp-1 text-vt-12 text-vt-text-muted">{b.notes}</div>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-vt-text-muted">{unitLabel(b)}</TableCell>
                      <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                        <div className="flex flex-col items-end gap-0.5">
                          <span>{formatInr(b.total_due_inr ?? b.amount_inr)}</span>
                          {(b.late_fee_inr > 0 || b.late_fee_inr_computed > 0) ? (
                            <span className="text-vt-12 text-vt-alert">
                              + ₹{(b.late_fee_inr || b.late_fee_inr_computed).toLocaleString('en-IN')} late
                              {b.late_fee_applied_at ? '' : ' (pending)'}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-vt-text-muted">{formatDateTime(b.due_date)}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[b.status]}>{STATUS_LABEL[b.status]}</Badge>
                      </TableCell>
                      <TableCell>
                        {b.receipt_number ? (
                          <button
                            type="button"
                            onClick={() => openReceiptByBill(b)}
                            className="inline-flex items-center gap-vt-1 rounded-vt-sm bg-[color:var(--vt-safe-100)] px-2 py-0.5 text-vt-11 font-medium text-[color:var(--vt-safe)] hover:opacity-80"
                            title="Open receipt PDF"
                          >
                            <FileText className="h-3 w-3" />
                            <span className="font-mono">{b.receipt_number}</span>
                          </button>
                        ) : (
                          <span className="text-vt-12 text-vt-text-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-vt-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="View charge-type breakdown"
                            onClick={() => setBreakdownBill(b)}
                          >
                            <ListTree className="h-4 w-4" /> View
                          </Button>
                          {b.status === 'paid' && b.receipt_number ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              title={`Download receipt ${b.receipt_number}`}
                              onClick={() => openReceipt(b)}
                            >
                              <Download className="h-4 w-4" /> Receipt
                            </Button>
                          ) : null}
                          {b.status !== 'paid' && b.status !== 'waived' && b.late_fee_inr_computed > 0 && b.late_fee_inr === 0 ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Apply computed late fee + mark overdue"
                              disabled={applyLateFee.isPending}
                              onClick={() => applyLateFee.mutate(b.id)}
                            >
                              <TimerReset className="h-4 w-4" /> Apply late fee
                            </Button>
                          ) : null}
                          {b.status !== 'paid' ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              title="Mark paid"
                              disabled={updateStatus.isPending}
                              onClick={() => updateStatus.mutate({ id: b.id, status: 'paid' })}
                            >
                              <CheckCircle2 className="h-4 w-4" /> Mark paid
                            </Button>
                          ) : null}
                          {b.status !== 'waived' && b.status !== 'paid' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Mark waived"
                              disabled={updateStatus.isPending}
                              onClick={() => {
                                // Server requires a rationale in notes when waiving.
                                // If the bill already has notes we can skip the reason prompt.
                                const existing = (b.notes ?? '').trim();
                                if (existing) {
                                  updateStatus.mutate({ id: b.id, status: 'waived' });
                                } else {
                                  setWaiveTarget(b);
                                }
                              }}
                            >
                              <Ban className="h-4 w-4" /> Waive
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={remove.isPending}
                            title="Delete bill"
                            onClick={() => setDeleteTarget(b)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <CursorPaginator
                hasMore={Boolean(paginator.nextCursor)}
                loading={list.isFetching}
                itemsShown={bills.length}
                pageSize={filterState.page_size}
                onLoadMore={() => {
                  if (paginator.nextCursor) setCursor(paginator.nextCursor);
                }}
              />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Breakdown dialog — shows the charge-type line items for one bill */}
      <Dialog
        open={Boolean(breakdownBill)}
        onOpenChange={(v) => (v ? null : setBreakdownBill(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bill breakdown</DialogTitle>
            <DialogDescription>
              {breakdownBill ? (
                <>
                  {breakdownBill.period_label} · {unitLabel(breakdownBill)} ·{' '}
                  <span className="font-mono">{formatInr(breakdownBill.amount_inr)}</span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {breakdownQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : breakdownQuery.isError ? (
            <div className="text-vt-13 text-vt-alert">{getErrorMessage(breakdownQuery.error)}</div>
          ) : breakdownQuery.data ? (
            <BreakdownBody
              bill={breakdownQuery.data}
              existingReceipt={existingReceipt}
              receiptLoading={receiptForBillQuery.isLoading}
              onOpenReceiptPdf={openReceiptPdf}
              onGenerateReceipt={() => setReceiptTarget(breakdownQuery.data!)}
            />
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBreakdownBill(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waive bill — requires a rationale in notes (server-side audit requirement). */}
      <ConfirmDialog
        open={Boolean(waiveTarget)}
        onOpenChange={(v) => (v ? null : setWaiveTarget(null))}
        title="Waive bill"
        description={
          waiveTarget ? (
            <>
              Waive <strong>{waiveTarget.period_label}</strong> for{' '}
              <strong>{unitLabel(waiveTarget)}</strong>? The bill amount stops being owed.
            </>
          ) : null
        }
        confirmLabel="Waive bill"
        variant="warn"
        promptLabel="Reason for waiver"
        promptPlaceholder="e.g. Resident hardship — approved by treasurer"
        promptValidate={(v) => {
          const t = v.trim();
          if (t.length < 3) return 'A rationale of at least 3 characters is required.';
          if (t.length > NOTES_MAX) return `Rationale must be at most ${NOTES_MAX} characters.`;
          return null;
        }}
        loading={updateStatus.isPending}
        onConfirm={(reason) => {
          if (!waiveTarget) return;
          updateStatus.mutate(
            { id: waiveTarget.id, status: 'waived', notes: (reason ?? '').trim() },
            { onSuccess: () => setWaiveTarget(null) }
          );
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(v) => (v ? null : setDeleteTarget(null))}
        title="Delete bill"
        description={
          deleteTarget ? (
            <>
              Permanently delete bill <strong>{deleteTarget.period_label}</strong> for{' '}
              <strong>{unitLabel(deleteTarget)}</strong>? This action cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete bill"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
        }}
      />

      {/* Phase 3 — Issue receipt dialog. Asks payment method + ref + notes,
          then posts to /receipts. Server marks the bill paid + stamps the
          receipt_number; we auto-open the PDF on success. */}
      <Dialog
        open={Boolean(receiptTarget)}
        onOpenChange={(v) => {
          if (!v) {
            setReceiptTarget(null);
            setReceiptForm({ payment_method: 'cash', payment_ref: '', notes: '' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue receipt</DialogTitle>
            <DialogDescription>
              {receiptTarget ? (
                <>
                  Generate a tax receipt for <strong>{receiptTarget.period_label}</strong> ·{' '}
                  <strong>{unitLabel(receiptTarget)}</strong> ·{' '}
                  <span className="font-mono">
                    {formatInr(receiptTarget.total_due_inr ?? receiptTarget.amount_inr)}
                  </span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="receipt_method">Payment method</Label>
              <Select
                id="receipt_method"
                value={receiptForm.payment_method}
                onChange={(e) =>
                  setReceiptForm((f) => ({
                    ...f,
                    payment_method: e.target.value as PaymentMethod
                  }))
                }
              >
                {PAYMENT_METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="receipt_ref">
                Reference (cheque #, UPI ref, NEFT UTR — optional)
              </Label>
              <Input
                id="receipt_ref"
                maxLength={120}
                value={receiptForm.payment_ref}
                onChange={(e) =>
                  setReceiptForm((f) => ({ ...f, payment_ref: e.target.value }))
                }
                placeholder="e.g. UPI/123456789012 or Cheque 048213"
              />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="receipt_notes">Notes (optional)</Label>
              <Textarea
                id="receipt_notes"
                rows={2}
                maxLength={500}
                value={receiptForm.notes}
                onChange={(e) =>
                  setReceiptForm((f) => ({ ...f, notes: e.target.value }))
                }
                placeholder="Anything special about this payment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setReceiptTarget(null);
                setReceiptForm({ payment_method: 'cash', payment_ref: '', notes: '' });
              }}
              disabled={issueReceipt.isPending}
            >
              Cancel
            </Button>
            <Button
              loading={issueReceipt.isPending}
              loadingText="Issuing…"
              disabled={issueReceipt.isPending || !receiptTarget}
              onClick={() => {
                if (!receiptTarget) return;
                const amount =
                  receiptTarget.total_due_inr ?? receiptTarget.amount_inr;
                issueReceipt.mutate({
                  bill_id: receiptTarget.id,
                  amount_inr: amount,
                  payment_method: receiptForm.payment_method,
                  payment_ref: receiptForm.payment_ref.trim() || undefined,
                  notes: receiptForm.notes.trim() || undefined
                });
              }}
            >
              Issue receipt + open PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Read-only breakdown body for the bill detail dialog. Splits the bill into
 * line items (with GST sub-rows when applicable) + a totals block + a
 * "Generate / View receipt" CTA when the bill is paid.
 */
function BreakdownBody(props: {
  bill: Bill & { line_items: BillLineItem[] };
  existingReceipt: PaymentReceiptRow | null;
  receiptLoading: boolean;
  onOpenReceiptPdf: (receiptId: string) => void;
  onGenerateReceipt: () => void;
}): JSX.Element {
  const { bill, existingReceipt, receiptLoading, onOpenReceiptPdf, onGenerateReceipt } = props;
  const lineItems = bill.line_items ?? [];

  // Derive totals. Prefer the server-denormalised values; fall back to
  // recomputing from the line items when older rows are missing them.
  const subtotal =
    bill.subtotal_inr ??
    lineItems.reduce(
      (s, li) =>
        s + Math.max(0, li.base_amount_inr ?? li.amount_inr ?? 0),
      0
    );
  const gstTotal =
    bill.gst_total_inr ??
    lineItems.reduce((s, li) => s + (li.gst_amount_inr ?? 0), 0);
  const rebateTotal =
    bill.rebate_total_inr ??
    lineItems.reduce(
      (s, li) =>
        s +
        Math.max(0, -(li.base_amount_inr ?? li.amount_inr ?? 0)),
      0
    );
  const lateFee = bill.late_fee_inr_computed ?? bill.late_fee_inr ?? 0;
  const grandTotal = bill.total_due_inr ?? bill.amount_inr + lateFee;

  return (
    <div className="flex flex-col gap-vt-3">
      <div className="overflow-hidden rounded-vt-md border border-vt-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Charge type</TableHead>
              <TableHead>Code</TableHead>
              <TableHead className="text-right">Base</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Line total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((li) => {
              const base = li.base_amount_inr ?? li.amount_inr ?? 0;
              const gst = li.gst_amount_inr ?? 0;
              const isRebate = base < 0;
              return (
                <TableRow key={li.id}>
                  <TableCell className="font-medium text-vt-text">
                    {li.label}
                    {isRebate ? (
                      <span className="ml-vt-1 text-vt-11 text-[color:var(--vt-safe)]">
                        (rebate)
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="font-mono text-vt-12 text-vt-text-muted">
                    {li.charge_type?.code ?? '—'}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono text-vt-13 ${
                      isRebate ? 'text-[color:var(--vt-safe)]' : 'text-vt-text'
                    }`}
                  >
                    {isRebate ? `–${formatInr(-base)}` : formatInr(base)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-vt-12 text-vt-text-muted">
                    {gst > 0 ? (
                      <>
                        {formatInr(gst)}
                        {li.gst_pct ? (
                          <span className="ml-1 text-vt-11">@ {li.gst_pct}%</span>
                        ) : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                    {formatInr(li.amount_inr)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Totals block — receipt-style summary */}
      <div className="rounded-vt-md bg-vt-surface-2 p-vt-3 text-vt-13">
        <div className="flex items-center justify-between py-0.5">
          <span className="text-vt-text-muted">Subtotal</span>
          <span className="font-mono text-vt-text">{formatInr(subtotal)}</span>
        </div>
        {gstTotal > 0 ? (
          <div className="flex items-center justify-between py-0.5">
            <span className="text-vt-text-muted">GST total</span>
            <span className="font-mono text-vt-text">{formatInr(gstTotal)}</span>
          </div>
        ) : null}
        {rebateTotal > 0 ? (
          <div className="flex items-center justify-between py-0.5">
            <span className="text-vt-text-muted">Rebates</span>
            <span className="font-mono text-[color:var(--vt-safe)]">
              –{formatInr(rebateTotal)}
            </span>
          </div>
        ) : null}
        {lateFee > 0 ? (
          <div className="flex items-center justify-between py-0.5">
            <span className="text-vt-text-muted">
              Late fee
              {bill.late_fee_applied_at ? '' : ' (pending)'}
            </span>
            <span className="font-mono text-vt-alert">{formatInr(lateFee)}</span>
          </div>
        ) : null}
        <div className="my-vt-2 h-px bg-vt-border" />
        <div className="flex items-center justify-between">
          <span className="font-semibold text-vt-text">Grand total</span>
          <span className="font-mono text-vt-15 font-semibold text-vt-text">
            {formatInr(grandTotal)}
          </span>
        </div>
      </div>

      {/* Generate / View receipt CTA — only when the bill is settled. */}
      {bill.paid_at ? (
        <div className="flex items-center justify-between gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface p-vt-3">
          <div className="flex items-center gap-vt-2 text-vt-13">
            <FileText className="h-4 w-4 text-vt-text-muted" strokeWidth={1.75} />
            <div>
              <div className="font-medium text-vt-text">Tax receipt</div>
              <div className="text-vt-12 text-vt-text-muted">
                {existingReceipt
                  ? `Issued ${formatDateTime(existingReceipt.receipt_date)} · ${existingReceipt.receipt_number}`
                  : 'No receipt issued yet. Generate one to record the payment officially.'}
              </div>
            </div>
          </div>
          {existingReceipt ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onOpenReceiptPdf(existingReceipt.id)}
            >
              <FileText className="h-4 w-4" /> View receipt
            </Button>
          ) : (
            <Button size="sm" disabled={receiptLoading} onClick={onGenerateReceipt}>
              <FileText className="h-4 w-4" /> Generate receipt
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
